'use client';

import type { FC } from 'react';
import { useEffect } from 'react';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import type { ProductListVirtualPageFragmentFragmentDoc } from '@packages/gql/generated/shopping/ProductListVirtualPageFragmentFragmentDoc';

import { usePaging } from '../../hooks/usePaging';
import type { SearchProductCardProps } from '../SearchProductCard/SearchProductCard.client';
import { SearchProductCard } from '../SearchProductCard/SearchProductCard.client';
import type { CriteoProductCardProps } from '../CriteoProductCard/CriteoProductCard.client';
import { CriteoProductCard } from '../CriteoProductCard/CriteoProductCard.client';
import { CriteoBeaconImage } from '../CriteoBeaconImage/CriteoBeaconImage';

import { useGlycerinListingsEvents } from './useGlycerinListingsEvents';
import { useCriteoTracking } from './useCriteoTracking';

/* GraphQL */ `
  fragment ProductListVirtualPageFragment on SearchProductResult {
    sponsoredProducts {
      tracking {
        onLoad
        onView
      }
      items {
        styleId
        primaryVariationGroup {
          sku
        }
        ...CriteoProductCardItemFragment
      }
    }
    summary {
      totalResultCount
    }
    items {
      styleId
      ...SearchProductCardItemFragment
    }
    ...UsePagingFragment
    ...SearchProductCardResultFragment
    ...CriteoProductCardResultFragment
    ...ProductGridCriteoTrackingFragment
    ...GlycerinListingsEventsResultFragment
  }
`;

export type ProductListVirtualPageProps = {
  maskedResult: FragmentType<typeof ProductListVirtualPageFragmentFragmentDoc>;
  /**
   * Current page number, starting at 1 and incrementing by 1 for each page (for both regular paging and infinite scrolling, where usually there is no page number exposed to the user)
   */
  pageNumber: number;
  /**
   * Event fired when any card in the grid first becomes visible
   */
  onProductFirstBecameVisible?: (parameters: { pageNumber: number; indexInPage?: number }) => void;
  SponsoredProductCardComponent?: FC<CriteoProductCardProps>;
  StandardProductCardComponent?: FC<SearchProductCardProps>;
  // NOTE this is only here for storybook compat for now, because the wishlist button can't render in storybook due to technical issues with MUI icons
  hideWishlistButton?: boolean;
  isFirstVisiblePage?: boolean;
};

/**
 * ProductGrid component based on https://mui.com/material-ui/react-grid/
 * */
export const ProductListVirtualPage: FC<ProductListVirtualPageProps> = ({
  maskedResult,
  pageNumber,
  onProductFirstBecameVisible,
  SponsoredProductCardComponent = CriteoProductCard,
  StandardProductCardComponent = SearchProductCard,
  hideWishlistButton,
  isFirstVisiblePage,
}) => {
  const result = unmask<typeof ProductListVirtualPageFragmentFragmentDoc>(maskedResult);
  const { pageSize, currentPage } = usePaging(result);
  const itemPosition = (currentPage - 1) * pageSize + 1;
  const { totalResultCount } = result.summary;

  const { dispatchGlycerinListingsEvent } = useGlycerinListingsEvents({
    maskedResult: result,
    pageNumber,
  });
  const { dispatchCriteoTrackingEvent: dispatchCriteoTracking } = useCriteoTracking({
    maskedResult: result,
  });

  useEffect(() => {
    dispatchGlycerinListingsEvent();
    dispatchCriteoTracking();
    // TODO SEARCH-3512 figure out if this works with correct dependencies as well
    // The display event should be fired only once, when the virtual page is initially displayed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prerenderedProductCount = result.sponsoredProducts ? 4 : 8;
  // NOTE for now it is cleared with stakeholders to just use a fixed number of sponsored products
  // should this change in the future, it will be complex to always show "one row" again, because the layout is now css-based and the beacons must not fire when the card is not actually visible
  const sponsoredProductCount = 4;

  const hasSponsoredProducts = (result.sponsoredProducts?.items?.length ?? 0) > 0;

  return (
    <>
      {result.sponsoredProducts && (
        <>
          {result.sponsoredProducts.tracking.onLoad && (
            <CriteoBeaconImage
              src={result.sponsoredProducts.tracking.onLoad}
              name="criteo:onLoad"
            />
          )}
          {result.sponsoredProducts.tracking.onView && (
            <CriteoBeaconImage
              src={result.sponsoredProducts.tracking.onView}
              name="criteo:onView"
            />
          )}
        </>
      )}

      {result.sponsoredProducts?.items?.slice(0, sponsoredProductCount).map((item, index) => (
        <SponsoredProductCardComponent
          key={`criteo-${item.styleId}-${item.primaryVariationGroup.sku}`} // criteo cards can have the same styleId, so we need to include the sku in the key
          maskedResult={result}
          maskedItem={item}
          pageNumber={pageNumber}
          onFirstBecameVisible={onProductFirstBecameVisible}
          priority={isFirstVisiblePage && index === 0}
        />
      ))}

      {result.items.map((item, index) => (
        <StandardProductCardComponent
          key={item.styleId}
          aria-setsize={totalResultCount}
          aria-posinset={itemPosition + index}
          maskedItem={item}
          maskedResult={result}
          pageNumber={pageNumber}
          indexInPage={index}
          alwaysRender={index < prerenderedProductCount}
          onFirstBecameVisible={onProductFirstBecameVisible}
          hideWishlistButton={hideWishlistButton}
          priority={isFirstVisiblePage && !hasSponsoredProducts && index === 0}
        />
      ))}
    </>
  );
};
